// client/src/components/plataforma/planosFranquias/PlansFranquiasPage.js
import React, { Fragment, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFranquiaPlans, useMutateFranquiaPlans } from '../../../hooks/useFranquiaPlans';
import { Package, Plus, Loader2, Edit, Trash2, CheckCircle, XCircle, List, Star, Briefcase, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';

const formatPrice = (price) => {
    if (typeof price !== 'number') return 'N/A';
    return (price / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const PlansFranquiasPage = () => {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState('active');
    const [selectedPlano, setSelectedPlano] = useState(null);

    const { data: franchisePlans = [], isLoading, refetch } = useFranquiaPlans({ status: statusFilter === 'all' ? undefined : statusFilter });
    const { deletePlan } = useMutateFranquiaPlans();
    const isDeleting = deletePlan.isPending;

    const handleDeactivate = useCallback((plano) => {
        const activeFranquias = plano.activeFranquiasCount || 0;
        const confirmMessage = activeFranquias > 0 ?
            `Este plano possui ${activeFranquias} franquia(s) ativa(s). Deseja realmente desativá-lo?` :
            'Tem certeza que deseja desativar este plano?';

        if (!window.confirm(confirmMessage)) {
            return;
        }

        deletePlan.mutate(plano._id, {
            onSuccess: () => refetch()
        });
        setSelectedPlano(null);
    }, [deletePlan, refetch]);

    const hasPlans = franchisePlans.length > 0;

    const actions = selectedPlano ? [
        {
            key: 'editar',
            label: 'Editar plano',
            icon: <Edit size={20} className="icon" />,
            onClick: () => { navigate(`/plataforma/planos-franquias/${selectedPlano._id}/editar`); setSelectedPlano(null); },
        },
        ...(selectedPlano.isActive ? [{
            key: 'desativar',
            label: 'Desativar plano',
            icon: <Trash2 size={20} className="icon" />,
            onClick: () => handleDeactivate(selectedPlano),
            destructive: true,
            loading: isDeleting,
        }] : []),
    ] : [];

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Package />}
                    title="Planos de Franquia"
                    subtitle={`${franchisePlans.length} plano(s)`}
                    actions={
                        <>
                            <div className="view-switcher-group">
                                <button type="button" className={`action-button-icon ${statusFilter === 'active' ? 'is-active' : ''}`} onClick={() => setStatusFilter('active')} title="Ver planos ativos">
                                    <CheckCircle size={18} />
                                </button>
                                <button type="button" className={`action-button-icon ${statusFilter === 'inactive' ? 'is-active' : ''}`} onClick={() => setStatusFilter('inactive')} title="Ver planos inativos">
                                    <XCircle size={18} />
                                </button>
                                <button type="button" className={`action-button-icon ${statusFilter === 'all' ? 'is-active' : ''}`} onClick={() => setStatusFilter('all')} title="Ver todos os planos">
                                    <List size={18} />
                                </button>
                            </div>
                        </>
                    }
                />
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando planos...</span>
                        </div>
                    ) : hasPlans ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Nome do Plano</th>
                                    <th>Preço Mensal</th>
                                    <th style={{ textAlign: 'left' }}>Franquias Vinculadas</th>
                                    <th>Teste Grátis</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {franchisePlans.map((plano) => {
                                    const activeFranquias = plano.activeFranquias || [];
                                    const activeFranquiasCount = plano.activeFranquiasCount ?? activeFranquias.length;
                                    const extraCount = activeFranquiasCount - activeFranquias.length;

                                    return (
                                        <tr key={plano._id} onClick={() => setSelectedPlano(plano)} style={{ cursor: 'pointer' }}>
                                            <td className="font-medium">
                                                {plano.name}
                                                {plano.isDefaultTrial && (
                                                    <span title="Este é o plano padrão para novos testes" className="ml-2 text-yellow-500 inline-flex align-middle">
                                                        <Star size={16} fill="currentColor" />
                                                    </span>
                                                )}
                                            </td>
                                            <td>{formatPrice(plano.price)}</td>
                                            <td>
                                                {activeFranquiasCount === 0 ? (
                                                    <span className="text-gray-400 text-sm">Nenhuma franquia</span>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1 items-center">
                                                        {activeFranquias.map(franquia => (
                                                            <span
                                                                key={franquia._id}
                                                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                                                title={franquia.nome}
                                                            >
                                                                <Briefcase size={11} className="mr-1" />
                                                                {franquia.nome}
                                                            </span>
                                                        ))}
                                                        {extraCount > 0 && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                                +{extraCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{plano.trialPeriodDays > 0 ? `${plano.trialPeriodDays} dias` : <span className="text-gray-400 text-sm">N/A</span>}</td>
                                            <td>
                                                <span className={`badge ${plano.isActive ? 'badge-success' : 'badge-danger'}`}>
                                                    {plano.isActive ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setSelectedPlano(plano); }}
                                                    title="Ações"
                                                    className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <Package size={48} className="text-gray-400 mx-auto" />
                            <h3 className="text-lg font-semibold text-gray-700 mt-4 mb-2">Nenhum plano encontrado</h3>
                            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                                {statusFilter === 'active' ? 'Não há planos ativos no momento.' :
                                    statusFilter === 'inactive' ? 'Não há planos inativos no momento.' :
                                        'Não há planos cadastrados no sistema.'}
                            </p>
                            <button
                                onClick={() => navigate('/plataforma/planos-franquias/novo')}
                                className="action-button action-button-primary"
                            >
                                <Plus size={18} className="icon" /> Novo Plano
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ActionsDrawer
                open={!!selectedPlano}
                onClose={() => setSelectedPlano(null)}
                title={selectedPlano?.name || 'Plano'}
                subtitle={selectedPlano ? formatPrice(selectedPlano.price) : null}
                actions={actions}
            />

            <FabButton
                onClick={() => navigate('/plataforma/planos-franquias/novo')}
                title="Novo Plano de Franquia"
                visible={true}
                icon={<Package size={26} className="icon" />}
            />
        </Fragment>
    );
};

export default PlansFranquiasPage;
